import {
  AccessRole,
  AUTHORIZATION_ACTIONS,
  AUTHORIZATION_SUBJECTS,
  createSalonAbilityForUser,
  FranchiseAccess,
  UserContext,
} from "@salon/types/authorization";
import { useMemo } from "react";
import { FranchiseAccessRoleType } from "@/gql/graphql";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";

/**
 * Dashboard mode enum for user interface context
 */
export enum DashboardMode {
  WORKER = "WORKER",
  MANAGER = "MANAGER",
}

/**
 * Dashboard context interface defining user capabilities
 */
export interface DashboardContext {
  mode: DashboardMode;
  canUpdateSalonConfiguration: boolean;
}

/**
 * Hook to determine dashboard context based on current user and salon
 * Uses existing auth and salon store hooks internally
 *
 * @returns DashboardContext object with mode and permissions
 */
export function useDashboardContext(): DashboardContext {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const franchiseAccess = useSalonStore((state) => state.franchiseAccess);

  return useMemo(() => {
    // Default context when no salon is selected or no access
    if (
      !isAuthenticated ||
      !user ||
      !currentSalon ||
      !franchiseAccess ||
      franchiseAccess.length === 0
    ) {
      return {
        mode: DashboardMode.WORKER,
        canUpdateSalonConfiguration: false,
      };
    }

    // Find the user's role in the current salon
    let currentSalonRole: FranchiseAccessRoleType | null = null;

    for (const franchise of franchiseAccess) {
      const salon = franchise.franchise.salons.find((s) => s.id === currentSalon.id);
      if (salon) {
        currentSalonRole = salon.role;
        break;
      }
    }

    // If no role found for current salon, default to worker mode
    if (!currentSalonRole) {
      return {
        mode: DashboardMode.WORKER,
        canUpdateSalonConfiguration: false,
      };
    }

    // Convert FullFranchiseAccess to FranchiseAccess format for authorization service
    const franchiseAccesses: FranchiseAccess[] = franchiseAccess.map((fa) => ({
      franchiseId: fa.franchiseId,
      role: fa.role as AccessRole,
      isActive: fa.isActive,
      salons: fa.franchise.salons.map((salon) => ({
        salonId: salon.id,
        role: salon.role as AccessRole,
        isActive: salon.isActive,
      })),
    }));

    // Create UserContext for authorization service
    const userContext: UserContext = {
      userId: user.id,
      email: user.email,
      role: user.role,
      franchiseAccesses: franchiseAccesses,
    };

    // Get authorization ability for the current salon
    const ability = createSalonAbilityForUser(userContext, currentSalon.id);

    // Determine mode based on role hierarchy
    const managerRoles: FranchiseAccessRoleType[] = [
      "ADMIN",
      "OWNER",
      "MANAGER",
      "FRANCHISE_ADMIN",
      "FRANCHISE_VIEWER",
    ];
    const hasManagerCapabilities = currentSalonRole
      ? managerRoles.includes(currentSalonRole)
      : false;

    // Check salon configuration update permission
    const canUpdateSalonConfiguration = ability.can(
      AUTHORIZATION_ACTIONS.UPDATE,
      AUTHORIZATION_SUBJECTS.SALON_CONFIGURATION
    );

    return {
      mode: hasManagerCapabilities ? DashboardMode.MANAGER : DashboardMode.WORKER,
      canUpdateSalonConfiguration,
    };
  }, [isAuthenticated, user, currentSalon, franchiseAccess]);
}
